import React, { useEffect, useState } from 'react'
import { FaEnvelope, FaClock, FaCheckCircle, FaReply, FaHourglassHalf } from 'react-icons/fa'
import contactService from '../services/contactService'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import Loader from '../components/Loader'
import { formatDate } from '../utils/helpers'
import SEO from '../utils/SEO'

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: FaHourglassHalf, color: 'bg-yellow-100 text-yellow-800' },
  replied: { label: 'Replied', icon: FaReply, color: 'bg-blue-100 text-blue-800' },
  resolved: { label: 'Resolved', icon: FaCheckCircle, color: 'bg-green-100 text-green-800' },
}

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending
  const Icon = config.icon
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${config.color}`}
    >
      <Icon className="text-[10px]" />
      {config.label}
    </span>
  )
}

const MyEnquiries = () => {
  const [enquiries, setEnquiries] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeFilter, setActiveFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [expandedId, setExpandedId] = useState(null)

  useEffect(() => {
    loadEnquiries()
  }, [activeFilter, page])

  const loadEnquiries = async () => {
    try {
      setLoading(true)
      const params = { page, limit: 10 }
      if (activeFilter !== 'all') params.status = activeFilter

      const data = await contactService.getMyQueries(params)
      setEnquiries(data.queries || [])
      setTotalPages(data.totalPages || 1)
    } catch (error) {
      toast.error('Error loading enquiries')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (filter) => {
    setActiveFilter(filter)
    setPage(1)
  }

  const filters = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'replied', label: 'Replied' },
    { key: 'resolved', label: 'Resolved' },
  ]

  if (loading && enquiries.length === 0) {
    return <Loader />
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEO title="My Enquiries - Himachal Yatra" noindex />
      <ToastContainer />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-2">
            My Enquiries
          </h1>
          <p className="text-gray-600">
            Track the status of your contact queries and view replies.
          </p>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => handleFilterChange(f.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeFilter === f.key
                  ? 'bg-primary-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Enquiry list */}
        {enquiries.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
            <FaEnvelope className="mx-auto text-4xl text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No enquiries found</h3>
            <p className="text-gray-500">
              {activeFilter !== 'all'
                ? `You have no ${activeFilter} enquiries.`
                : "You haven't submitted any enquiries yet."}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {enquiries.map((enquiry) => (
              <div
                key={enquiry.id}
                className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-sm border border-white/30 overflow-hidden transition-shadow hover:shadow-md"
              >
                <button
                  onClick={() => setExpandedId(expandedId === enquiry.id ? null : enquiry.id)}
                  className="w-full text-left p-5 flex items-start justify-between gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-semibold text-gray-900 truncate">{enquiry.subject}</h3>
                      <StatusBadge status={enquiry.status} />
                    </div>
                    <p className="text-sm text-gray-500 flex items-center gap-1.5">
                      <FaClock className="text-xs" />
                      {formatDate(enquiry.created_at)}
                      {enquiry.query_type === 'quote_request' && (
                        <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                          Quote Request
                        </span>
                      )}
                    </p>
                  </div>
                  <span className="text-gray-400 mt-1 text-sm">
                    {expandedId === enquiry.id ? '▲' : '▼'}
                  </span>
                </button>

                {expandedId === enquiry.id && (
                  <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Your Message</p>
                      <p className="text-gray-700 whitespace-pre-wrap">{enquiry.message}</p>
                    </div>

                    {enquiry.admin_reply && (
                      <div className="bg-blue-50 rounded-xl p-4">
                        <p className="text-sm font-medium text-blue-800 mb-1 flex items-center gap-1.5">
                          <FaReply className="text-xs" /> Response from Himachal Yatra
                        </p>
                        <p className="text-blue-900 whitespace-pre-wrap">{enquiry.admin_reply}</p>
                        {enquiry.replied_at && (
                          <p className="text-xs text-blue-600 mt-2">
                            Replied on {formatDate(enquiry.replied_at)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default MyEnquiries
